// The widget's design tokens: one definition per theme, in one place.
//
// Before this file the dark palette was written out three times in the
// stylesheet (`:host`, `:host([widget-theme="auto"])`, and again inside the
// prefers-color-scheme block) and the light palette twice. They had already
// started to diverge — the `auto` block was missing `--speechify-agent-accent`
// — and nothing would have caught it, because a missing custom property
// silently inherits rather than failing.
//
// ── Relationship to the console's tokens ───────────────────────────────────
//
// The console's palette lives in libs/sws-ui/src/styles/globals.css as shadcn
// HSL triplets. The two agree on the surfaces that matter most and differ
// elsewhere; `tokens.test.ts` pins the agreement so it cannot rot, and the
// differences are listed here with the reason each one exists:
//
// The widget adopts the console's neutral ramp (AIS-5391, decided 2026-07-29).
// Backgrounds, foregrounds and muted all match; `tokens.test.ts` reads the
// console's stylesheet directly so neither side can drift from the other.
//
// What decided it was not taste. `--speechify-agent-muted` colours `.hint`,
// the error message a visitor is shown, at 12px — and the widget's old
// #71717a on the dark #0a0a0a surface scored **4.10**, under the 4.5 WCAG AA
// needs for text that size. The console's #a3a3a3 scores 7.85. So the ramp
// that made the two products match also fixed an accessibility failure
// shipping on every customer site in the default theme. `contrast.test.ts`
// now asserts the ratio rather than the hex, so a future palette edit that
// looks nicer but reads worse fails the build.
//
// The border is the one deliberate difference, and it is permanent. The
// console renders on a background it controls, so an opaque hairline is
// correct there; the widget renders on a page it has never seen, where an
// opaque border reads as a hard edge against an unexpected backdrop and a
// translucent one blends into whatever is behind it.

/** One theme's surface colours. Every field lands as a `--speechify-agent-*` custom property. */
export interface Palette {
  bg: string;
  fg: string;
  accent: string;
  muted: string;
  border: string;
  shadow: string;
  shadowHover: string;
}

/** Brand accent, shared by both themes: the orb gradient and the focus ring. */
export const BRAND_ACCENT = "#6366f1";

/** Default orb gradient stops. Overridden per embed via the avatar-orb-color-* attributes. */
export const DEFAULT_ORB_COLOR_1 = "#6366f1";
export const DEFAULT_ORB_COLOR_2 = "#ec4899";

/** Fully round: the collapsed pill and the FAB are both capsules. */
export const RADIUS = "9999px";

// ── The non-colour scales ──────────────────────────────────────────────────
//
// Extracted from what the stylesheet already used rather than invented, so
// nothing moved by a pixel when they landed. They exist to make the scale
// ENFORCEABLE: `scale.test.ts` asserts that every radius and every transition
// duration in the built stylesheet comes from these sets, so the next value
// somebody reaches for has to either be on the scale or be a deliberate
// addition to it. That is the whole mechanism - a design system is not a file
// of constants, it is a constraint that fails a build.
//
// Two inconsistencies were found in the extraction and are DELIBERATELY
// preserved rather than quietly rounded off:
//
//   - radius 18px on the transcript bubble, with no 18 anywhere else and a 16
//     right next to it;
//   - 200ms on the popup control against 220ms on the button, with no reason
//     for both.
//
// Both are 2px and 20ms - almost certainly accidents, and almost certainly
// imperceptible to fix. "Almost certainly imperceptible" is exactly the
// reasoning that has no place in a change to what customers already see, so
// they are named here, kept on the scale, and left for a deliberate call.

/** Spacing steps, all multiples of 4, as the stylesheet already used them. */
export const SPACE = {
  xs: "4px",
  sm: "8px",
  md: "12px",
  lg: "20px",
  xl: "32px",
} as const;

/** Corner radii. `pill` and `circle` are shapes; the rest are a size ramp. */
export const RADII = {
  pill: RADIUS,
  circle: "50%",
  lg: "16px",
  /** Transcript panel. */
  panel: "12px",
  /** Transcript bubble. The odd one out - see the note above. */
  bubble: "18px",
  md: "8px",
  sm: "4px",
} as const;

/** Type sizes the widget's own chrome uses. Content inside the terms modal is em-relative. */
export const FONT_SIZE = {
  base: "14px",
  small: "12px",
} as const;

// Deliberately NOT tokenised: the orb's keyframe loop durations.
//
// They run 1.1s, 1.2s, 1.4s, 1.6s, 1.8s, 2s, 2.2s, 2.4s, 3s - which reads like
// drift until you notice what they are. Each is a per-state tempo: the orb
// pulses faster while the agent speaks than while it listens, with thinking
// between the two. That is tuned craft, and putting it on a shared four-step
// ramp would flatten the one part of the widget that is genuinely expressive.
//
// Worth recording how close this came to being a scale anyway: the extraction
// regex read `1.1s` as `1s`, which made nine tuned values look like a tidy
// integer sequence, and an invented ramp nearly got imposed on them. A scale
// is only worth enforcing where one actually exists.

/** Motion. Durations plus the two easings, so a new animation cannot invent a third. */
export const MOTION = {
  fast: "150ms",
  /** Popup control. Sits 20ms from `base` for no reason - see the note above. */
  quick: "200ms",
  base: "220ms",
  slow: "300ms",
  ease: "ease",
  emphasis: "cubic-bezier(0.4, 0, 0.2, 1)",
} as const;

export const DARK: Palette = {
  bg: "#0a0a0a",
  fg: "#fafafa",
  accent: BRAND_ACCENT,
  muted: "#a3a3a3",
  border: "rgba(255, 255, 255, 0.08)",
  shadow: "0 2px 12px rgba(0, 0, 0, 0.25), 0 0 0 1px rgba(255, 255, 255, 0.06)",
  shadowHover: "0 6px 20px rgba(0, 0, 0, 0.35), 0 0 0 1px rgba(255, 255, 255, 0.1)",
};

export const LIGHT: Palette = {
  bg: "#ffffff",
  fg: "#0a0a0a",
  accent: BRAND_ACCENT,
  muted: "#737373",
  border: "rgba(0, 0, 0, 0.08)",
  shadow: "0 2px 12px rgba(0, 0, 0, 0.08), 0 0 0 1px rgba(0, 0, 0, 0.06)",
  shadowHover: "0 6px 20px rgba(0, 0, 0, 0.12), 0 0 0 1px rgba(0, 0, 0, 0.08)",
};

/**
 * Emits a palette as CSS custom properties, for interpolation into a
 * selector body. Indented to match the surrounding stylesheet so the built
 * CSS stays readable in devtools on a customer's page — which is where we
 * read it, since we cannot open their build.
 */
export function paletteCSS(p: Palette, indent = "    "): string {
  return [
    `${indent}--speechify-agent-bg: ${p.bg};`,
    `${indent}--speechify-agent-fg: ${p.fg};`,
    `${indent}--speechify-agent-accent: ${p.accent};`,
    `${indent}--speechify-agent-muted: ${p.muted};`,
    `${indent}--speechify-agent-border: ${p.border};`,
    `${indent}--speechify-agent-shadow: ${p.shadow};`,
    `${indent}--speechify-agent-shadow-hover: ${p.shadowHover};`,
  ].join("\n");
}
